import React, { useState } from 'react';
import { DataTable, DataTableExpandedRows } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import {
  SalaryHistoryDbType,
  SalaryItemRate,
} from '../../../../api/types/accountants/salary-history/salary-history.type';
import { LegalEntityType } from '../../../user-page/roles/lecturer-price-rate/componenets/acountant/user-page-lecturer-rate-accountant';
import dateUtils from '../../../../utils/date-format';
import { SalaryExportDialog } from './salary-export-dialog';
import { LecturerRateDisplayType } from '../../../../api/types/lecturers/price-rate/lecturer-rate-display-type';

interface SalaryHistoryTableProps {
  salariesData: SalaryHistoryDbType[];
}

export const SalaryHistoryTable: React.FC<SalaryHistoryTableProps> = ({
  salariesData,
}) => {
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>({});
  const [selectedRows, setSelectedRows] = useState<SalaryHistoryDbType[]>([]);
  const [showExportDialog, setShowExportDialog] = useState(false);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
    }).format(value);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('he-IL').format(new Date(date));
  };

  const nameTemplate = (rowData: SalaryHistoryDbType) => {
    return `${rowData.firstName} ${rowData.lastName}`;
  };

  const totalAmountTemplate = (rowData: SalaryHistoryDbType) => {
    const total = rowData.items.reduce(
      (sum, item) =>
        sum +
        item.rate *
          item.hoursNumber *
          (item.displayType === LecturerRateDisplayType.OneHour ? 1 : 4 / 3),
      0,
    );
    return formatCurrency(total);
  };

  const itemsCountTemplate = (rowData: SalaryHistoryDbType) => {
    return rowData.items.length;
  };

  // Helper function to get unique legal entity types for a user
  const getLegalEntityTypesTemplate = (rowData: SalaryHistoryDbType) => {
    const uniqueTypes = [
      ...new Set(rowData.items.map((item) => item.legalEntityType)),
    ];
    if (uniqueTypes.length === 1) {
      return LegalEntityType.getDisplayText(uniqueTypes[0]);
    }
    return uniqueTypes
      .map((type) => LegalEntityType.getDisplayText(type))
      .join(', ');
  };

  // Helper function to get unique reporting codes for a user
  const getReportingCodesTemplate = (rowData: SalaryHistoryDbType) => {
    const uniqueCodes = [
      ...new Set(rowData.items.map((item) => item.reportingCode)),
    ];
    return uniqueCodes.join(', ');
  };

  // Helper function to get unique legal entity IDs for a user
  const getLegalEntityIdsTemplate = (rowData: SalaryHistoryDbType) => {
    const uniqueIds = [
      ...new Set(rowData.items.map((item) => item.legalEntityId)),
    ];
    return uniqueIds.join(', ');
  };

  // Helper function for sorting by legal entity type
  const getLegalEntityTypeForSorting = (rowData: SalaryHistoryDbType) => {
    // Get the most common legal entity type, or the first one if all are unique
    const types = rowData.items.map((item) => item.legalEntityType);
    const typeCount = types.reduce(
      (acc, type) => {
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      },
      {} as Record<LegalEntityType, number>,
    );

    const mostCommonType = Object.entries(typeCount).sort(
      ([, a], [, b]) => b - a,
    )[0][0] as LegalEntityType;
    return LegalEntityType.getDisplayText(mostCommonType);
  };

  const expandedRowTemplate = (data: SalaryHistoryDbType) => {
    return (
      <div>
        <h5 style={{ display: 'flex', justifyContent: 'right' }}>
          פירוט תשלומים
        </h5>
        <DataTable value={data.items} responsiveLayout="scroll" size="small">
          <Column field="id" header="מזהה" style={{ textAlign: 'right' }} />
          <Column
            field="rate"
            header="תעריף"
            body={(rowData: SalaryItemRate) => formatCurrency(rowData.rate)}
            style={{ textAlign: 'right' }}
          />

          <Column
            field="rate"
            header="כמות"
            body={(rowData: SalaryItemRate) => {
              const hours =
                (new Date(rowData.endAt).getTime() -
                  new Date(rowData.startAt).getTime()) /
                (1000 * 60 * 60);
              const formatted =
                hours % 1 === 0 ? hours.toString() : hours.toFixed(1);
              return <label>{formatted} שעות</label>;
            }}
            style={{ textAlign: 'right' }}
          />
          <Column
            field="activityType"
            header="סוג פעילות"
            style={{ textAlign: 'right' }}
          />
          <Column
            field="legalEntityType"
            header="סוג ישות משפטית"
            style={{ textAlign: 'right' }}
            body={(rowDate: SalaryItemRate) => (
              <div>
                {LegalEntityType.getDisplayText(rowDate.legalEntityType)}
              </div>
            )}
          />
          <Column
            field="reportingCode"
            header="קוד דיווח במערכת שכר"
            style={{ textAlign: 'right' }}
          />
          <Column
            field="legalEntityId"
            header="מזהה ישות משפטית"
            style={{ textAlign: 'right' }}
          />
          <Column
            field="createdByName"
            header="מפיק משכורת"
            style={{ textAlign: 'right' }}
          />
          <Column
            field="createdAt"
            header="תאריך יצירה"
            body={(rowData: SalaryItemRate) =>
              dateUtils.displayFormatDate(rowData.createdAt)
            }
            style={{ textAlign: 'right' }}
          />
        </DataTable>
      </div>
    );
  };

  const expanderTemplate = (rowData: SalaryHistoryDbType) => {
    const isExpanded = expandedRows[rowData.id] !== undefined;
    return (
      <button
        type="button"
        onClick={() => {
          const newExpandedRows = { ...expandedRows };
          if (isExpanded) {
            delete newExpandedRows[rowData.id];
          } else {
            newExpandedRows[rowData.id] = true;
          }
          setExpandedRows(newExpandedRows);
        }}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '14px',
          padding: '4px',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isExpanded ? '▼' : '▶'}
      </button>
    );
  };

  const handleExportClick = () => {
    if (selectedRows.length === 0) {
      return;
    }
    setShowExportDialog(true);
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
        }}
      >
        <h2>היסטוריית משכורות</h2>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>
            נבחרו {selectedRows.length} מתוך {salariesData.length} רשומות
          </span>
          <Button
            label="יצוא לאקסל"
            icon="pi pi-file-excel"
            onClick={handleExportClick}
            disabled={selectedRows.length === 0}
            style={{
              backgroundColor: selectedRows.length > 0 ? '#10B981' : '#9CA3AF',
              border: 'none',
              padding: '0.5rem 1rem',
            }}
          />
        </div>
      </div>

      <DataTable
        value={salariesData}
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data as DataTableExpandedRows)}
        selection={selectedRows}
        onSelectionChange={(e) => setSelectedRows(e.value)}
        selectionMode="multiple"
        responsiveLayout="scroll"
        dataKey="id"
        rowExpansionTemplate={expandedRowTemplate}
        tableStyle={{ minWidth: '80rem' }}
        emptyMessage={<div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>}
      >
        <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
        <Column
          expander={false}
          body={expanderTemplate}
          style={{ width: '3rem', textAlign: 'center' }}
        />
        <Column
          header="שם המרצה"
          body={nameTemplate}
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          field="nationalId"
          header="תעודת זהות"
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          field="email"
          header="דוא״ל"
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          header="סוג ישות משפטית"
          body={getLegalEntityTypesTemplate}
          sortable
          sortFunction={(event) => {
            const data = [...event.data];
            return data.sort((a, b) => {
              const aType = getLegalEntityTypeForSorting(a);
              const bType = getLegalEntityTypeForSorting(b);
              return event.order === 1
                ? aType.localeCompare(bType, 'he')
                : bType.localeCompare(aType, 'he');
            });
          }}
          style={{ textAlign: 'right', minWidth: '150px' }}
        />
        <Column
          header="קוד דיווח"
          body={getReportingCodesTemplate}
          sortable
          style={{ textAlign: 'right', minWidth: '120px' }}
        />
        <Column
          header="מזהה ישות משפטית"
          body={getLegalEntityIdsTemplate}
          sortable
          style={{ textAlign: 'right', minWidth: '150px' }}
        />
        <Column
          header="מספר תשלומים"
          body={itemsCountTemplate}
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          header="סכום כולל"
          body={totalAmountTemplate}
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          field="createdAt"
          header="תאריך יצירה"
          body={(rowData: SalaryHistoryDbType) => formatDate(rowData.createdAt)}
          sortable
          style={{ textAlign: 'right' }}
        />
      </DataTable>

      <SalaryExportDialog
        visible={showExportDialog}
        onHide={() => setShowExportDialog(false)}
        selectedData={selectedRows}
      />
    </div>
  );
};
